/* global google */
/** @jsx jsx */
import React from 'react';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import Downshift from 'downshift';
import defaultsDeep from 'lodash/defaultsDeep';
import { withNamespaces } from 'react-i18next';
import Lottie from 'react-lottie';
import Spacer from '../../../ui/Spacer';
import searchAnimation from '../../../../assets/animations/search-load-grey.json';
import DotComponent from '../../../ui/DotComponent';
import { Container, Row, Column } from '../../../../styles/Grid';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import { STATUS_OK, PLACES_READY_EVENT } from '../../../../utils/constants';
import {
  DYNAMIC_PAGE,
  MINIMUM_CHARACTERS_TO_SEARCH,
  RESEARCH_PARAMETER,
  SEARCH_PARAMETER,
} from '../../../../utils/constants';
import * as styles from './index.styles';

const animationOptions = {
  loop: true,
  autoplay: false,
  animationData: searchAnimation,
};

const fetchAutocompletePlaces = (autocompleteService, options) =>
  new Promise((resolve, reject) => {
    autocompleteService.getQueryPredictions(options, (results, status) => {
      if (status === STATUS_OK) {
        resolve(results);
      } else {
        reject([results, status]);
      }
    });
  });

const fetchPlaceData = (geocodeService, placeId) =>
  new Promise((resolve, reject) => {
    geocodeService.geocode({ placeId }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const [place] = results;
        resolve(place);
      } else {
        reject(results, status);
      }
    });
  });

const getLatLngFromPlace = place => {
  const location = get(place, 'geometry.location');

  if (!location) {
    return;
  }

  return {
    lat: location.lat(),
    lng: location.lng(),
  };
};

export const defaultData = {
  BackgroundColor: { fields: { Value: { value: '' } } },
  BodyDescription: { value: '' },
  Headline: { value: '' },
};

export class ResultsHero extends SafePureComponent {
  searchRef = React.createRef();

  autocompleteService = null;
  geocoder = null;
  state = {
    suggestions: [],
    isLoading: false,
    isFocus: false,
  };

  constructor(props) {
    super(props);

    // Fix issue on safari where loading animation doesn't reset when going
    // back in history.
    if (typeof window !== 'undefined') {
      window.onpageshow = event => {
        if (event.persisted) {
          window.location.reload();
        }
      };
    }
  }

  componentDidMount() {
    if (window.placesAPIReady) {
      this.initServices();
    } else {
      window.addEventListener(PLACES_READY_EVENT, this.initServices);
    }
  }

  initServices = () => {
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.geocoder = new google.maps.Geocoder();
  };

  onFormSubmit = e => {
    e.preventDefault();

    const { lng: language } = this.props;
    const searchValue = this.searchRef.current.value;
    const url = `/${language}/${DYNAMIC_PAGE}?${SEARCH_PARAMETER}=${searchValue}&${RESEARCH_PARAMETER}=true`;

    window.location.href = url;
  };

  fetchSuggestions = async input => {
    const { autocompleteService } = this;
    const autocompleteOptions = { input };

    const places = await fetchAutocompletePlaces(
      autocompleteService,
      autocompleteOptions,
    ).catch(() => []);

    const suggestions = places.map(({ description, place_id }) => ({
      id: place_id,
      description,
    }));

    this.setState({ suggestions });
  };

  searchSuggestionPlace = async item => {
    const { geocoder } = this;
    const { id } = item;
    const { lng: language } = this.props;

    this.setState({ isLoading: true });

    const place = await fetchPlaceData(geocoder, id);
    const { lat, lng } = getLatLngFromPlace(place);
    const url = `/${language}/${DYNAMIC_PAGE}?lat=${lat}&long=${lng}&rs=true`;

    window.location.href = url;
  };

  renderSearchInput = () => {
    const { t } = this.props;
    const { suggestions, isLoading, isFocus } = this.state;

    return (
      <Downshift
        onChange={this.searchSuggestionPlace}
        itemToString={item => get(item, 'description', '')}
      >
        {({
          getMenuProps,
          getItemProps,
          getRootProps,
          getInputProps,
          isOpen,
          highlightedIndex,
        }) => (
          <div
            {...getRootProps({}, { suppressRefError: true })}
            css={[
              styles.searchInputContainer,
              isFocus && !isLoading && styles.searchInputContainerFocus,
            ]}
          >
            <input
              ref={this.searchRef}
              css={styles.searchInput}
              placeholder={t('PlaceholderTextSearch')}
              disabled={isLoading}
              {...getInputProps({
                onChange: this.onInputChange,
              })}
              onFocus={this.onInputFocus}
              onBlur={this.onInputBlur}
            />
            <span css={styles.animationContainer}>
              <Lottie
                options={animationOptions}
                isClickToPauseDisabled
                height={50}
                width={75}
                isStopped={!isLoading}
              />
            </span>
            {!isLoading && (
              <button
                data-testid="form-submit"
                css={styles.searchIconButton}
                type="submit"
              >
                <i css={styles.searchIcon} className="icon-search" />
              </button>
            )}
            {isOpen && Boolean(suggestions.length) && (
              <ul css={styles.suggestionsContainer} {...getMenuProps()}>
                {suggestions.map((item, index) => (
                  <li
                    css={[
                      styles.suggestion,
                      highlightedIndex === index &&
                        styles.highlightedSuggestion,
                    ]}
                    key={`place-${item.id}`}
                    {...getItemProps({ item, index })}
                  >
                    <i css={styles.suggestionIcon} className="icon-pin" />
                    {item.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Downshift>
    );
  };

  onInputChange = debounce(() => {
    const input = this.searchRef.current.value;

    if (input.length >= MINIMUM_CHARACTERS_TO_SEARCH) {
      this.fetchSuggestions(input);
    } else {
      this.setState({ suggestions: [] });
    }
  }, 300);

  onInputFocus = () => {
    this.setState({ isFocus: true });
  };

  onInputBlur = () => {
    this.setState({ isFocus: false });
  };

  safeRender() {
    const fields = defaultsDeep(this.props.fields, defaultData);
    const { BackgroundColor, Headline, BodyDescription } = fields;

    return (
      <Spacer
        marginCss={styles.spacerMargin}
        paddingCss={styles.spacerPadding}
        backgroundColor={BackgroundColor}
      >
        <Container>
          <Row>
            <Column cols={{ sm: 6, md: 8, lg: 12, xl: 8 }}>
              <h1 css={styles.heroTitle}>
                <Text field={Headline} />
                <DotComponent />
              </h1>
            </Column>
          </Row>
          <Row css={styles.aligner}>
            <Column
              css={styles.bodyWrapper}
              cols={{ sm: 6, md: 8, lg: 12, xl: 8 }}
            >
              <p css={styles.bodyText}>
                <Text field={BodyDescription} />
              </p>
            </Column>
            <Column
              css={styles.searchWrapper}
              cols={{ sm: 6, md: 5, lg: 6, xl: 5 }}
            >
              <form onSubmit={this.onFormSubmit}>
                {this.renderSearchInput()}
              </form>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

ResultsHero.defaultProps = {
  fields: defaultData,
  t: key => key,
};

ResultsHero.propTypes = {
  fields: PropTypes.shape({
    BackgroundColor: PropTypes.shape({
      fields: PropTypes.shape({
        Value: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    }),
    BodyDescription: PropTypes.shape({
      value: PropTypes.string,
    }),
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
};

ResultsHero.displayName = 'ResultsHero';

export default withNamespaces()(ResultsHero);
